/**
 * Ad Tracking Service
 *
 * Orchestrates ad platform providers (Google Ads, LinkedIn, Reddit, Twitter).
 * Port of useAdTracking composable from Nuxt 3 → class-based for Nuxt 2.
 */

import type { AdTrackingProvider, AdProviderFilter, ConversionEventType } from '~/types/tracking'
import type { CookieConsentService } from './CookieConsentService'

const isDev = process.env.NODE_ENV === 'development'
const MAX_PENDING_CONVERSIONS = 50

export class AdTrackingService {
  private providers: AdTrackingProvider[] = []
  private initialized = false
  private consentService: CookieConsentService
  private pendingConversions: Array<{
    conversionType: ConversionEventType | string
    params?: Record<string, unknown>
    filter?: AdProviderFilter
  }> = []

  constructor(consentService: CookieConsentService) {
    this.consentService = consentService
  }

  registerProvider(provider: AdTrackingProvider): void {
    if (this.providers.some((p) => p.name === provider.name)) {
      if (isDev) {
        console.warn(`Ad tracking provider "${provider.name}" already registered`)
      }
      return
    }

    this.providers.push(provider)

    if (isDev) {
      console.log(`Ad tracking provider registered: ${provider.name}`)
    }
  }

  initializeProviders(): void {
    if (!this.consentService.hasConsent('advertising')) {
      if (isDev) {
        console.log('Ad tracking: No advertising consent, skipping initialization')
      }
      return
    }

    for (const provider of this.providers) {
      if (!provider.isAvailable()) {
        try {
          provider.init()
        } catch (error) {
          if (isDev) {
            console.warn(`Failed to initialize ad tracking provider "${provider.name}":`, error)
          }
        }
      }
    }

    this.initialized = true
    this.retryFlushPendingConversions()
  }

  trackPageView(url?: string, filter?: AdProviderFilter): void {
    const targets = this.getAvailableProviders(filter)
    for (const provider of targets) {
      try {
        provider.trackPageView(url)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to track page view in ${provider.name}:`, error)
        }
      }
    }
  }

  trackEvent(eventName: string, params?: Record<string, unknown>, filter?: AdProviderFilter): void {
    const targets = this.getAvailableProviders(filter)
    for (const provider of targets) {
      try {
        provider.trackEvent(eventName, params)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to track event in ${provider.name}:`, error)
        }
      }
    }
  }

  trackConversion(
    conversionType: ConversionEventType | string,
    params?: Record<string, unknown>,
    filter?: AdProviderFilter
  ): void {
    const targets = this.getAvailableProviders(filter)

    if (
      targets.length === 0 &&
      this.initialized &&
      this.providers.length > 0 &&
      this.consentService.hasConsent('advertising')
    ) {
      if (this.pendingConversions.length >= MAX_PENDING_CONVERSIONS) return
      this.pendingConversions.push({ conversionType, params, filter })

      if (isDev) {
        console.log(
          `Queued conversion "${conversionType}" until ad providers are available`,
          params
        )
      }
      return
    }

    for (const provider of targets) {
      try {
        provider.trackConversion(conversionType, params)
      } catch (error) {
        if (isDev) {
          console.warn(`Failed to track conversion in ${provider.name}:`, error)
        }
      }
    }

    if (isDev && targets.length > 0) {
      console.log(
        `Conversion "${conversionType}" tracked across ${targets.length} provider(s)`,
        params
      )
    }
  }

  isAvailable(): boolean {
    return this.getAvailableProviders().length > 0
  }

  private getAvailableProviders(filter?: AdProviderFilter): AdTrackingProvider[] {
    if (!this.consentService.hasConsent('advertising')) {
      return []
    }

    const available = this.providers.filter((p) => p.isAvailable())
    return filter ? available.filter(filter) : available
  }

  private flushPendingConversions(): void {
    if (!this.pendingConversions.length) return

    const pending = [...this.pendingConversions]
    this.pendingConversions = []

    for (const conversion of pending) {
      this.trackConversion(conversion.conversionType, conversion.params, conversion.filter)
    }
  }

  private retryFlushPendingConversions(retries = 10, delayMs = 500): void {
    let attempts = 0

    const interval = setInterval(() => {
      attempts += 1
      this.flushPendingConversions()

      if (!this.pendingConversions.length || attempts >= retries) {
        clearInterval(interval)
      }
    }, delayMs)
  }
}
